/**
 * VisibilityTab — ERP Workspace "Module Visibility" tab.
 * Per-industry or per-country module enable/disable toggles.
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Search, ToggleLeft, ToggleRight, Building2, Globe } from 'lucide-react';
import { API, authFetch } from '../../lib/api';
import { erpToast } from '../../components/erp/ERPToast';

export function VisibilityTab({ modules }) {
  const [viewMode,   setViewMode]   = useState('industry');
  const [industries, setIndustries] = useState([]);
  const [countries,  setCountries]  = useState([]);
  const [selected,   setSelected]   = useState('');
  const [mappings,   setMappings]   = useState({});
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(new Set());
  const [filterQ,    setFilterQ]    = useState('');

  useEffect(() => {
    authFetch(`${API}/uams/industries`).then(r => r.json()).then(d => setIndustries(Array.isArray(d) ? d : [])).catch(() => {});
    authFetch(`${API}/country-config?page=1&pageSize=220`).then(r => r.json()).then(d => setCountries(Array.isArray(d?.rows) ? d.rows : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selected) { setMappings({}); return; }
    setLoading(true);
    const param    = viewMode === 'industry' ? `industryCode=${encodeURIComponent(selected)}` : `countryCode=${encodeURIComponent(selected)}`;
    const endpoint = viewMode === 'industry' ? `${API}/erp-workspace/admin/industry-modules?${param}` : `${API}/erp-workspace/admin/country-modules?${param}`;
    authFetch(endpoint).then(r => r.json()).then(d => {
      if (Array.isArray(d)) { const m = {}; d.forEach(row => { m[row.ModuleId] = row.IsAllowed !== false; }); setMappings(m); }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selected, viewMode]);

  const toggleModule = useCallback(async (moduleId, currentValue) => {
    if (!selected) return;
    setSaving(s => new Set(s).add(moduleId));
    const newVal   = !currentValue;
    const body     = viewMode === 'industry' ? { industryCode: selected, moduleId, isAllowed: newVal } : { countryCode: selected, moduleId, isAllowed: newVal };
    const endpoint = viewMode === 'industry' ? `${API}/erp-workspace/admin/industry-modules` : `${API}/erp-workspace/admin/country-modules`;
    try {
      const r = await authFetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json();
      if (d.ok) {
        setMappings(m => ({ ...m, [moduleId]: newVal }));
        erpToast.success(`${viewMode === 'industry' ? 'Industry' : 'Country'} module ${newVal ? 'enabled' : 'disabled'}`);
      } else { erpToast.error(d.error); }
    } catch (e) { erpToast.error(e.message); }
    setSaving(s => { const ns = new Set(s); ns.delete(moduleId); return ns; });
  }, [selected, viewMode]);

  const filtered = useMemo(() => {
    const q = filterQ.toLowerCase();
    return modules.filter(m => !q || m.ModuleName?.toLowerCase().includes(q));
  }, [modules, filterQ]);

  const options  = viewMode === 'industry' ? industries : countries;
  const optLabel = viewMode === 'industry' ? 'IndustryName' : 'CountryName';
  const optVal   = viewMode === 'industry' ? 'IndustryCode' : 'CountryCode';

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          <button onClick={() => { setViewMode('industry'); setSelected(''); }}
            style={{ padding: '5px 14px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', background: viewMode === 'industry' ? 'var(--erp-primary,#3b82f6)' : 'transparent', color: viewMode === 'industry' ? '#fff' : 'var(--text-secondary)', border: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Building2 size={11} /> Industry
          </button>
          <button onClick={() => { setViewMode('country'); setSelected(''); }}
            style={{ padding: '5px 14px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', background: viewMode === 'country' ? 'var(--erp-primary,#3b82f6)' : 'transparent', color: viewMode === 'country' ? '#fff' : 'var(--text-secondary)', border: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Globe size={11} /> Country
          </button>
        </div>
        <select value={selected} onChange={e => setSelected(e.target.value)}
          data-testid="visibility-selector"
          style={{ flex: 1, maxWidth: 280, fontSize: '0.78rem', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6 }}>
          <option value="">— Select {viewMode === 'industry' ? 'Industry' : 'Country'} —</option>
          {options.map(o => <option key={o[optVal]} value={o[optVal]}>{o[optLabel]} ({o[optVal]})</option>)}
        </select>
        {selected && (
          <div style={{ position: 'relative' }}>
            <Search size={11} style={{ position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input value={filterQ} onChange={e => setFilterQ(e.target.value)} placeholder="Filter modules…"
              style={{ paddingLeft: 22, fontSize: '0.75rem', padding: '4px 4px 4px 22px', border: '1px solid var(--border)', borderRadius: 5, width: 160 }} />
          </div>
        )}
        {selected && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>{Object.values(mappings).filter(Boolean).length} / {modules.length} modules active</span>}
      </div>

      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', padding: '6px 10px', background: 'var(--surface-2)', borderRadius: 6, border: '1px solid var(--border)' }}>
        <strong>How it works:</strong> Modules not mapped here default to <em>visible for all</em>. Toggle OFF to hide for the selected {viewMode}.
      </div>

      {!selected && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          Select an {viewMode === 'industry' ? 'industry' : 'country'} above to manage module visibility.
          {modules.length === 0 && <div style={{ marginTop: 8, color: '#f59e0b', fontSize: '0.78rem' }}>No modules loaded yet — run Init Tables + Sync first.</div>}
        </div>
      )}

      {selected && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)', borderBottom: '2px solid var(--border)' }}>
                {['Module ID', 'Module Name', 'App Type', `Visible for "${selected}"`, ''].map(h => (
                  <th key={h} style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 700, color: 'var(--text-secondary)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading mappings…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>No modules found.</td></tr>
              ) : filtered.map(m => {
                const isAllowed = mappings[m.ModuleId] !== false;
                return (
                  <tr key={m.ModuleId} style={{ borderBottom: '1px solid var(--border)', background: isAllowed ? 'transparent' : 'rgba(239,68,68,.03)', opacity: m.IsActive ? 1 : 0.5 }}>
                    <td style={{ padding: '6px 12px', fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: '0.72rem' }}>{m.ModuleId}</td>
                    <td style={{ padding: '6px 12px', fontWeight: 600 }}>{m.ModuleName}</td>
                    <td style={{ padding: '6px 12px', color: 'var(--text-muted)', fontSize: '0.72rem' }}>App {m.AppType || 1}</td>
                    <td style={{ padding: '6px 12px' }}>
                      <button onClick={() => toggleModule(m.ModuleId, isAllowed)}
                        disabled={saving.has(m.ModuleId)}
                        data-testid={`visibility-toggle-${m.ModuleId}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, opacity: saving.has(m.ModuleId) ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {isAllowed
                          ? <><ToggleRight size={22} color="#22c55e" /><span style={{ fontSize: '0.72rem', color: '#22c55e' }}>Visible</span></>
                          : <><ToggleLeft  size={22} color="#94a3b8" /><span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Hidden</span></>}
                      </button>
                    </td>
                    <td style={{ padding: '6px 12px', width: 20 }}>
                      {saving.has(m.ModuleId) && <span className="spin" style={{ display:'inline-block', width:11, height:11, border:'2px solid #94a3b8', borderTopColor:'transparent', borderRadius:'50%' }} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
